import { forwardRef, useCallback, useId, type ChangeEvent } from 'react'
import { cn } from '../../utils.js'

export type QuantityUnit = 'piece' | 'dozen' | 'lb' | 'oz' | 'kg' | 'g'

export interface QuantitySelectorProps {
  /** Current quantity value */
  value: number
  /** Called when quantity changes */
  onChange: (value: number) => void
  /** Unit type determines the UI: stepper for countable, input for weight */
  unit: QuantityUnit
  /** Unit label displayed next to the input */
  unitLabel?: string
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max?: number
  /** Step increment for weight units */
  step?: number
  /** Visible label */
  label?: string
  /** Disabled state */
  disabled?: boolean
  /** Additional className */
  className?: string
}

const COUNTABLE_UNITS: QuantityUnit[] = ['piece', 'dozen']

function isCountable(unit: QuantityUnit): boolean {
  return COUNTABLE_UNITS.includes(unit)
}

/**
 * Quantity selector that adapts to unit type.
 * - Countable units (piece, dozen): number stepper with +/- buttons
 * - Weight units (lb, oz, kg, g): numeric input field
 */
export const QuantitySelector = forwardRef<HTMLDivElement, QuantitySelectorProps>(
  function QuantitySelector(
    {
      value,
      onChange,
      unit,
      unitLabel,
      min = 0,
      max,
      step,
      label,
      disabled = false,
      className,
    },
    ref,
  ) {
    const id = useId()

    const resolvedStep = step ?? (isCountable(unit) ? 1 : 0.25)
    const displayLabel = unitLabel ?? unit

    const clamp = useCallback(
      (v: number) => {
        let clamped = v
        if (min != null) clamped = Math.max(min, clamped)
        if (max != null) clamped = Math.min(max, clamped)
        return Number(clamped.toFixed(2))
      },
      [min, max],
    )

    const increment = useCallback(() => {
      onChange(clamp(value + resolvedStep))
    }, [value, resolvedStep, clamp, onChange])

    const decrement = useCallback(() => {
      onChange(clamp(value - resolvedStep))
    }, [value, resolvedStep, clamp, onChange])

    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseFloat(e.target.value)
        if (!isNaN(parsed)) {
          onChange(clamp(parsed))
        }
      },
      [clamp, onChange],
    )

    const atMin = min != null && value <= min
    const atMax = max != null && value >= max

    return (
      <div ref={ref} className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium text-[var(--color-text)]"
          >
            {label}
          </label>
        )}
        <div className="flex items-center gap-2">
          {isCountable(unit) ? (
            <>
              <button
                type="button"
                onClick={decrement}
                disabled={disabled || atMin}
                aria-label="Decrease quantity"
                className={cn(
                  'inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-md)] border border-[var(--color-border)]',
                  'text-[var(--color-text)] hover:bg-[var(--color-surface)] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z" clipRule="evenodd" />
                </svg>
              </button>
              <span
                id={id}
                role="status"
                aria-live="polite"
                className="min-w-[3rem] text-center text-sm font-medium text-[var(--color-text)]"
              >
                {value}
              </span>
              <button
                type="button"
                onClick={increment}
                disabled={disabled || atMax}
                aria-label="Increase quantity"
                className={cn(
                  'inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-md)] border border-[var(--color-border)]',
                  'text-[var(--color-text)] hover:bg-[var(--color-surface)] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
              </button>
            </>
          ) : (
            <input
              id={id}
              type="number"
              value={value}
              onChange={handleInputChange}
              min={min}
              max={max}
              step={resolvedStep}
              disabled={disabled}
              aria-label={label ?? `Quantity in ${displayLabel}`}
              className={cn(
                'w-24 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors px-3 py-2 text-sm',
              )}
            />
          )}
          <span className="text-sm text-[var(--color-text-secondary)]">{displayLabel}</span>
        </div>
      </div>
    )
  },
)
